import { showSnackBar, updateUploadProgress as updateUploadProgressAction, completeUpload } from '../redux/Slices/snackbarSlice';

/**
 * Normalize any thrown/rejected value into a clean, user-facing string.
 * Errors come in many shapes — Error objects, rejected-thunk payloads, axios
 * error objects, plain strings, or `{ message }`. Concatenating them (`'...' +
 * error`) yields "[object Object]". Use this to always show something readable.
 *
 * @param {*} error - the caught error / rejected value
 * @param {string} fallback - message to show when nothing useful can be extracted
 */
export const getErrorMessage = (error, fallback = 'Something went wrong. Please try again.') => {
    if (!error) return fallback;
    if (typeof error === 'string') return error;
    // axios-style: error.response.data.message
    const apiMsg = error?.response?.data?.message || error?.data?.message;
    if (apiMsg && typeof apiMsg === 'string') return apiMsg;
    if (typeof error?.message === 'string' && error.message) return error.message;
    return fallback;
};

// Store undo actions outside of Redux to avoid serialization issues
const undoActionsMap = new Map();

export const createUndoAction = (actionType, data, dispatch) => {
    return () => {
        switch (actionType) {
            case 'DELETE_CARD':
                // Restore deleted card
                dispatch(restoreCard(data));
                break;
            case 'ARCHIVE_CARD':
                // Unarchive card
                dispatch(unarchiveCard(data));
                break;
            case 'REMOVE_MEMBER':
                // Re-add member
                dispatch(addMember(data));
                break;
            case 'DELETE_LIST':
                // Restore deleted list
                dispatch(restoreList(data));
                break;
            case 'DELETE_BOARD':
                // Restore deleted board
                dispatch(restoreBoard(data));
                break;
            case 'CLEAR_FILTERS':
                // This case is handled by the component that has access to clearFilters function
                // We'll emit a custom event that the Board component can listen to
                window.dispatchEvent(new CustomEvent('clearFilters'));
                break;
            default:
                console.warn('Unknown undo action type:', actionType);
        }
    };
};

export const showSuccessWithUndo = (dispatch, message, undoActionType, undoData) => {
    const undoAction = createUndoAction(undoActionType, undoData, dispatch);

    // Generate a unique ID for this snackbar
    const snackbarId = `snackbar_${Date.now()}_${Math.random()}`;

    // Store the undo action function outside of Redux
    undoActionsMap.set(snackbarId, undoAction);

    dispatch(showSnackBar({
        id: snackbarId,
        message,
        type: 'success',
        duration: 8000,
        hasUndo: true, // Flag to indicate undo is available
        undoLabel: 'Undo'
    }));
};

// Function to execute and cleanup undo action
export const executeUndoAction = (snackbarId) => {
    const undoAction = undoActionsMap.get(snackbarId);
    if (undoAction) {
        undoAction();
        undoActionsMap.delete(snackbarId);
    }
};

// Function to cleanup undo action when snackbar is dismissed
export const cleanupUndoAction = (snackbarId) => {
    undoActionsMap.delete(snackbarId);
};

export const showSuccess = (dispatch, message, duration = 5000) => {
    dispatch(showSnackBar({
        message,
        type: 'success',
        duration
    }));
};

export const showError = (dispatch, message, duration = 7000) => {
    dispatch(showSnackBar({
        message,
        type: 'error',
        duration
    }));
};

export const showWarning = (dispatch, message, duration = 6000) => {
    dispatch(showSnackBar({
        message,
        type: 'warning',
        duration
    }));
};

export const showInfo = (dispatch, message, duration = 5000) => {
    dispatch(showSnackBar({
        message,
        type: 'info',
        duration
    }));
};

export const showUploading = (dispatch, message, uploadId = null) => {
    const id = uploadId || `upload_${Date.now()}_${Math.random()}`;

    dispatch(showSnackBar({
        id,
        message,
        type: 'uploading',
        persistent: true,
        uploadProgress: { percentage: '0', remainingTime: '' }
    }));

    return id; // Return the ID so it can be used to update progress
};

export const updateUploadProgress = (dispatch, uploadId, progressData) => {
    dispatch(updateUploadProgressAction({
        id: uploadId,
        uploadProgress: progressData
    }));
};

// Complete upload and show success
export const completeUploadProgress = (dispatch, uploadId) => {
    // First ensure 100% progress is shown
    updateUploadProgress(dispatch, uploadId, {
        percentage: '100.0',
        remainingTime: 'Completed'
    });

    // Then convert to success after a brief delay
    setTimeout(() => {
        dispatch(completeUpload({ id: uploadId }));
    }, 500);
};

// Migration helper - converts old toast calls to new snackbar calls
export const showToast = (dispatch, { message, type }) => {
    const typeMapping = {
        success: 'success',
        error: 'error',
        warning: 'warning',
        info: 'info'
    };

    dispatch(showSnackBar({
        message,
        type: typeMapping[type] || 'info',
        duration: 5000
    }));
};

// Placeholder action creators - these should be imported from actual slices
const restoreCard = (data) => ({ type: 'RESTORE_CARD', payload: data });
const unarchiveCard = (data) => ({ type: 'UNARCHIVE_CARD', payload: data });
const addMember = (data) => ({ type: 'ADD_MEMBER', payload: data });
const restoreList = (data) => ({ type: 'RESTORE_LIST', payload: data });
const restoreBoard = (data) => ({ type: 'RESTORE_BOARD', payload: data });
